/**
 * Keyboard-shortcut hook (PHASE1 §7.2). Binds a `key → handler` map to
 * `window` keydown. Shortcuts are ignored while an input, textarea, select,
 * button, link, or contenteditable element is focused (so typing an amount
 * never fires `s`, and Enter/Space always activate the focused control
 * natively instead of being hijacked), for OS key-repeat (holding a key must
 * not mass-fire mutations), for already-handled events, and while `enabled`
 * is false (e.g. a modal is open).
 */
import { useEffect, useRef } from 'react';

export type HotkeyMap = Record<string, (event: KeyboardEvent) => void>;

/** Focused controls own their keyboard interaction — hotkeys must not fire. */
function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  // Native activation (Enter on a focused BucketChip, nav link, …) wins over
  // any hotkey; `closest` also covers content nested inside the control.
  return target.closest('button, a, [role="button"]') !== null;
}

export function useHotkeys(map: HotkeyMap, enabled = true): void {
  const mapRef = useRef(map);

  useEffect(() => {
    mapRef.current = map;
  });

  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(event: KeyboardEvent) {
      // OS key-repeat: one physical keypress = one action, never a burst.
      if (event.repeat) return;
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isInteractiveTarget(event.target)) return;
      const handler = mapRef.current[event.key];
      if (!handler) return;
      event.preventDefault();
      handler(event);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
